import React, { useEffect, useState, useRef, useMemo } from "react";
import { Button, Space, message, Modal, Tabs } from "antd";
import {
  ThunderboltOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from "@ant-design/icons";
import {
  orderArchers,
  sortArchersByScore,
  sumPoints,
} from "../../_helpers/utils";
import FirestoreService from "../../_helpers/firestoreService";
import ScoreBoard from "./ScoreBoard";
import "./board.scss";

interface BoardProps {
  competition: any;
  competitors: any[];
  zurhaiTotal: number;
  referees: number[];
  ranks: any[];
  teams: any[];
  loading: boolean;
  currentUserId?: number;
  userRole?: string;
  isFinished?: boolean;
}

const Board: React.FC<BoardProps> = ({
  competition,
  competitors,
  zurhaiTotal,
  referees,
  ranks,
  teams,
  loading,
  currentUserId,
  userRole,
  isFinished = false,
}) => {
  const boardRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = async () => {
    if (!boardRef.current) return;

    try {
      if (!document.fullscreenElement) {
        await boardRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.error("Error toggling fullscreen:", error);
      message.error("Бүтэн дэлгэц горимд шилжихэд алдаа гарлаа");
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);
  const [currentPage, setCurrentPage] = useState(1);
  const [zurhaiPages, setZurhaiPages] = useState<any[][]>([]);
  const [allowedZurhais, setAllowedZurhais] = useState<number[]>([]);
  const [isCreatingZurkhai, setIsCreatingZurkhai] = useState(false);
  const [zurkhaiExists, setZurkhaiExists] = useState(false);
  const [checkingExistence, setCheckingExistence] = useState(true);
  const [currentZurkhaiScores, setCurrentZurkhaiScores] = useState<
    Map<number, any>
  >(new Map());

  // Check if zurkhai already exists in Firestore
  useEffect(() => {
    const checkZurkhaiExistence = async () => {
      if (!competition?.id) return;

      try {
        setCheckingExistence(true);
        const exists = await FirestoreService.competitionExists(competition.id);
        setZurkhaiExists(exists);
      } catch (error) {
        console.error("Error checking zurkhai existence:", error);
      } finally {
        setCheckingExistence(false);
      }
    };

    checkZurkhaiExistence();
  }, [competition?.id]);

  // Calculate which zurhais this referee can access
  useEffect(() => {
    if (
      userRole === "referee" &&
      currentUserId &&
      referees &&
      referees.length > 0
    ) {
      // Find the zurhai index(es) where this referee is assigned
      const myZurhais: number[] = [];
      referees.forEach((refId, index) => {
        if (refId === currentUserId) {
          myZurhais.push(index + 1); // zurhai pages are 1-indexed
        }
      });
      setAllowedZurhais(myZurhais);

      // Automatically navigate to the first assigned zurhai
      if (myZurhais.length > 0) {
        setCurrentPage(myZurhais[0]);
      }
    } else {
      // For non-referee users, allow access to all zurhais
      setAllowedZurhais(Array.from({ length: zurhaiTotal }, (_, i) => i + 1));
    }
  }, [userRole, currentUserId, referees, zurhaiTotal]);

  // Subscribe to current zurkhai scores
  useEffect(() => {
    if (!competition?.id || !currentPage || !zurkhaiExists) return;

    const unsubscribe = FirestoreService.subscribeToZurkhaiScores(
      competition.id,
      currentPage,
      (scoresData) => {
        const scoresMap = new Map();
        scoresData.forEach((score) => {
          scoresMap.set(score.userId, score);
        });
        setCurrentZurkhaiScores(scoresMap);
      }
    );

    return () => unsubscribe();
  }, [competition?.id, currentPage, zurkhaiExists]);

  useEffect(() => {
    if (!competitors || competitors.length === 0 || !zurhaiTotal) {
      setZurhaiPages([]);
      return;
    }

    // Sort all competitors using the same logic as Archers tab
    const sortedCompetitors = orderArchers([...competitors]);

    // Distribute archers round-robin across zurhai pages
    const pages: any[][] = Array.from({ length: zurhaiTotal }, () => []);

    sortedCompetitors.forEach((archer, index) => {
      const pageIndex = index % zurhaiTotal;
      pages[pageIndex].push(archer);
    });

    setZurhaiPages(pages);

    // Reset to first allowed page if current page is out of bounds
    if (currentPage > zurhaiTotal) {
      const firstAllowedPage =
        allowedZurhais.length > 0 ? allowedZurhais[0] : 1;
      setCurrentPage(firstAllowedPage);
    }
  }, [competitors, zurhaiTotal, allowedZurhais]);

  // Get current archers for this zurkhai
  // IMPORTANT: Keep original distribution order - DO NOT sort by score!
  const currentArchers = useMemo(() => {
    // Get archers from zurhaiPages distribution (maintains round-robin order)
    const archersFromDistribution = zurhaiPages[currentPage - 1] || [];

    // If zurkhai exists in Firestore, merge with score data but KEEP ORIGINAL ORDER
    if (zurkhaiExists && currentZurkhaiScores.size > 0) {
      // Enrich archers with Firestore data in the same order as distribution
      const enrichedArchers = archersFromDistribution.map((archer: any) => {
        const scoreData = currentZurkhaiScores.get(archer.id);
        if (scoreData) {
          return {
            ...archer,
            hanaPoints: scoreData?.hanaPoints || [],
            hasPoints: scoreData?.hasPoints || [],
            totalScore: scoreData?.totalScore || 0,
            tovkhCount: scoreData?.tovkhCount || 0,
          };
        }
        return archer;
      });

      // Return in ORIGINAL order - no sorting!
      return enrichedArchers;
    }

    archersFromDistribution.sort((a,b)=> a.index - b.index);

    // If zurkhai doesn't exist yet, show from zurhaiPages distribution
    return archersFromDistribution;
  }, [zurhaiPages, currentPage, currentZurkhaiScores, zurkhaiExists]);

  const getRankDisplay = (archer: any) => {
    if (!archer.rank || !archer.rank2) return "";

    if (archer.rank.order <= 9) {
      return archer.rank.name;
    } else if (archer.rank.order > archer.rank2.order) {
      if (archer.rank.order <= 20 && archer.rank.order >= 12) {
        return `${archer.rank2.name}, ${archer.rank.name}`;
      } else {
        return archer.rank2.name;
      }
    } else {
      return archer.rank.name;
    }
  };

  // Handle creating zurkhai in Firestore
  const handleCreateZurkhai = async () => {
    if (!competition?.id || !currentUserId) {
      message.error("Тэмцээний мэдээлэл дутуу байна");
      return;
    }

    const isAdmin = userRole === "admin" || userRole === "superadmin";
    const isMainReferee = competition.referee_id === currentUserId;

    // Check permissions
    if (!isAdmin && !isMainReferee) {
      message.error("Та самбар зурах эрхгүй байна");
      return;
    }

    // If exists and user is not admin/superadmin, show error
    if (zurkhaiExists && !isAdmin) {
      message.error("Самбар аль хэдийн үүссэн байна");
      return;
    }

    // If admin/superadmin and exists, confirm reset
    if (zurkhaiExists && isAdmin) {
      Modal.confirm({
        title: "Самбар дахин үүсгэх",
        content:
          "Самбар аль хэдийн үүссэн байна. Хуучин өгөгдлийг устгаад шинээр үүсгэх үү?",
        okText: "Тийм",
        cancelText: "Үгүй",
        onOk: async () => {
          await createZurkhaiStructure();
        },
      });
      return;
    }

    await createZurkhaiStructure();
  };

  const createZurkhaiStructure = async () => {
    try {
      setIsCreatingZurkhai(true);

      const isAdmin = userRole === "admin" || userRole === "superadmin";

      // IMPORTANT: Use sorted competitors (same as zurhaiPages) for distribution
      // This ensures Firestore has the same order as Самбар -> Жагсаалт
      const sortedCompetitors = orderArchers([...competitors]);

      // Transform sorted competitors to the format expected by FirestoreService
      const competitorsData = sortedCompetitors.map((comp: any) => ({
        id: comp.id,
        firstName: comp.firstName,
        lastName: comp.lastName,
        teamId: comp.teamId,
        team: comp.team,
        rankId: comp.rankId,
        rank: comp.rank,
        gender: comp.gender,
      }));

      await FirestoreService.createZurkhai(
        competition.id,
        zurhaiTotal,
        competition.onog_ue || 10,
        competitorsData,
        referees,
        currentUserId!,
        isAdmin,
        {
          name: competition.name,
          location: competition.location,
          startDate: competition.startDate || competition.start_date,
          endDate: competition.endDate || competition.end_date,
        }
      );

      message.success("Самбар амжилттай үүслээ!");
      setZurkhaiExists(true);
    } catch (error: any) {
      console.error("Error creating zurkhai:", error);
      message.error(error.message || "Самбар үүсгэхэд алдаа гарлаа");
    } finally {
      setIsCreatingZurkhai(false);
    }
  };

  if (loading) {
    return <div>Уншиж байна...</div>;
  }

  if (zurhaiTotal === 0) {
    return <div>Зурхайн тоо тохируулаагүй байна.</div>;
  }

  // Helper functions for navigation
  const getPreviousPage = () => {
    const currentIndex = allowedZurhais.indexOf(currentPage);
    if (currentIndex > 0) {
      return allowedZurhais[currentIndex - 1];
    }
    return currentPage;
  };

  const getNextPage = () => {
    const currentIndex = allowedZurhais.indexOf(currentPage);
    if (currentIndex < allowedZurhais.length - 1) {
      return allowedZurhais[currentIndex + 1];
    }
    return currentPage;
  };

  const isFirstPage = () => {
    return allowedZurhais.indexOf(currentPage) === 0;
  };

  const isLastPage = () => {
    return allowedZurhais.indexOf(currentPage) === allowedZurhais.length - 1;
  };

  // Determine if user can enter scores
  const canEnterScores = () => {
    if (
      userRole === "admin" ||
      userRole === "superadmin" ||
      competition?.referee_id === currentUserId
    ) {
      return true;
    }
    // Check if this user is assigned to this zurkhai
    const refereesIds = referees || [];
    return refereesIds[currentPage - 1] === currentUserId;
  };

  return (
    <div className="board-container">
      {/* Show "Самбар зурах" button for admin/superadmin or main referee, but hide when finished */}
      {!isFinished &&
        (userRole === "admin" ||
          userRole === "superadmin" ||
          competition?.referee_id === currentUserId) && (
          <div style={{ marginBottom: 20 }}>
            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleCreateZurkhai}
              loading={isCreatingZurkhai || checkingExistence}
              disabled={
                zurkhaiExists &&
                userRole !== "admin" &&
                userRole !== "superadmin"
              }
            >
              {zurkhaiExists ? "Самбар дахин зурах" : "Самбар зурах"}
            </Button>
            {zurkhaiExists &&
              userRole !== "admin" &&
              userRole !== "superadmin" && (
                <span style={{ marginLeft: 12, color: "#52c41a" }}>
                  ✓ Самбар үүссэн
                </span>
              )}
          </div>
        )}

      {/* If zurkhai exists and user can enter scores, show scoring interface */}
      {zurkhaiExists && canEnterScores() ? (
        <div
          ref={boardRef}
          style={{
            backgroundColor: isFullscreen ? "#fff" : "transparent",
            minHeight: isFullscreen ? "100vh" : "auto",
            padding: isFullscreen ? "20px" : "0",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: 16,
            }}
          >
            <div style={{ flex: 1 }}>
              <Tabs
                defaultActiveKey="scoring"
                tabBarExtraContent={
                  <Button
                    type="default"
                    icon={
                      isFullscreen ? (
                        <FullscreenExitOutlined />
                      ) : (
                        <FullscreenOutlined />
                      )
                    }
                    onClick={toggleFullscreen}
                    style={{
                      marginLeft: 16,
                      alignSelf: "flex-start",
                      marginTop: 8,
                    }}
                  >
                    {isFullscreen ? "Бүтэн дэлгэцээс гарах" : "Бүтэн дэлгэц"}
                  </Button>
                }
                items={[
                  {
                    key: "scoring",
                    label: "Онооны самбар",
                    children: (
                      <ScoreBoard
                        competition={competition}
                        competitors={currentArchers}
                        zurkhaiIndex={currentPage}
                        currentUserId={currentUserId!}
                        userRole={userRole!}
                        onogUe={competition?.onog_ue || 10}
                        zurhaiTotal={zurhaiTotal}
                        onZurkhaiChange={setCurrentPage}
                        isFinished={isFinished}
                      />
                    ),
                  },
                  {
                    key: "list",
                    label: "Жагсаалт",
                    children: renderSimpleBoard(),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      ) : (
        renderSimpleBoard()
      )}
    </div>
  );

  function renderSimpleBoard() {
    return (
      <div>
        {/* Only show navigation if referee has multiple zurhais OR user is not a referee */}
        {(userRole !== "referee" || allowedZurhais.length > 1) && (
          <div style={{ marginBottom: 20 }}>
            <Space>
              <Button
                onClick={() => setCurrentPage(getPreviousPage())}
                disabled={isFirstPage()}
              >
                Өмнөх
              </Button>
              <span style={{ padding: "0 16px" }}>
                Зурхай {currentPage}{" "}
                {userRole !== "referee" && `/ ${zurhaiTotal}`}
              </span>
              <Button
                onClick={() => setCurrentPage(getNextPage())}
                disabled={isLastPage()}
              >
                Дараах
              </Button>
            </Space>
          </div>
        )}

        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #ddd",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  №
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Овог
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Нэр
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Хүйс
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Баг
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: "12px",
                    textAlign: "left",
                    fontWeight: 600,
                  }}
                >
                  Цол
                </th>
              </tr>
            </thead>
            <tbody>
              {currentArchers.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      border: "1px solid #ddd",
                      padding: "12px",
                      textAlign: "center",
                      color: "#999",
                    }}
                  >
                    Энэ зурхайд харваач байхгүй байна
                  </td>
                </tr>
              ) : (
                currentArchers.map((archer, index) => (
                  <tr
                    key={archer.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "12px",
                      }}
                    >
                      {index + 1}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "12px",
                      }}
                    >
                      {archer.lastName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "12px",
                      }}
                    >
                      {archer.firstName}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "12px",
                      }}
                    >
                      {archer.gender === "male" ? "Эрэгтэй" : "Эмэгтэй"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "12px",
                      }}
                    >
                      {archer.team?.name || ""}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: "12px",
                      }}
                    >
                      {getRankDisplay(archer)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 20 }}>
          <p>
            {userRole !== "referee" && (
              <>Нийт харваачдын тоо: {competitors?.length || 0} | </>
            )}
            Энэ зурхайн харваачдын тоо: {currentArchers.length}
          </p>
        </div>
      </div>
    );
  }
};

export default Board;
